import React, { useState } from "react";
import {
  addDoc,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import db from "../../utils/firebase.config";
import {
  createGuestDraft,
  createWardDraft,
  ensureUniqueSlug,
  getFirestoreErrorMessage,
  getSlugSeedFromGuest,
  normalizeWards,
  slugify,
} from "./helpers";

const CreatePage = () => {
  const [familyAlias, setFamilyAlias] = useState("");
  const [guest, setGuest] = useState(createGuestDraft());
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdInvitation, setCreatedInvitation] = useState(null);

  const baseUrl = window.location.origin;

  const updateGuestField = (field, value) => {
    setGuest((current) => {
      const next = { ...current, [field]: value };
      if (field === "name" && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const updateWardField = (wardIndex, field, value) => {
    setGuest((current) => {
      const nextWards = [...current.wards];
      nextWards[wardIndex] = { ...nextWards[wardIndex], [field]: value };
      return { ...current, wards: nextWards };
    });
  };

  const addWard = () => {
    setGuest((current) => ({ ...current, wards: [...current.wards, createWardDraft()] }));
  };

  const removeWard = (wardIndex) => {
    setGuest((current) => ({
      ...current,
      wards: current.wards.filter((_, index) => index !== wardIndex),
    }));
  };

  const validate = () => {
    if (!familyAlias.trim()) {
      return "Debes capturar el alias de familia";
    }
    if (!guest.name.trim()) {
      return "Falta nombre del invitado";
    }
    if (!guest.mobile.trim() || !/^\d{10}$/.test(guest.mobile.trim())) {
      return "El teléfono del invitado debe tener 10 dígitos";
    }

    for (const [wardIndex, ward] of guest.wards.entries()) {
      if (!ward.name.trim()) {
        return `Falta nombre del acompañante #${wardIndex + 1}`;
      }
    }

    return "";
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }

      const duplicatedMobileQuery = query(
        collectionGroup(db, "guess"),
        where("mobile", "==", guest.mobile.trim())
      );
      const duplicatedMobileSnapshot = await getDocs(duplicatedMobileQuery);
      if (!duplicatedMobileSnapshot.empty) {
        setError("El número de teléfono ya existe en otra invitación");
        return;
      }

      const now = new Date().toISOString();
      const invitationCreated = await addDoc(collection(db, "invitation"), {
        familyAlias: familyAlias.trim(),
        createdAt: now,
        updatedAt: now,
      });

      const slug = ensureUniqueSlug(getSlugSeedFromGuest(guest), new Set());
      const guestPayload = {
        name: guest.name.trim(),
        slug,
        confirmed: guest.confirmed === true,
        mobile: guest.mobile.trim(),
        table: (guest.table || "").trim(),
        wards: normalizeWards(guest.wards),
        createdAt: now,
        updateAt: now,
        updatedAt: now,
      };

      const guestCreated = await addDoc(
        collection(db, `invitation/${invitationCreated.id}/guess`),
        guestPayload
      );

      const created = {
        id: invitationCreated.id,
        familyAlias: familyAlias.trim(),
        guests: [{ id: guestCreated.id, ...guestPayload }],
      };

      setCreatedInvitation(created);
      setSuccess("Invitación creada correctamente");
      setFamilyAlias("");
      setGuest(createGuestDraft());
      setSlugTouched(false);
    } catch (saveError) {
      console.error("create invitation error", saveError);
      setError(getFirestoreErrorMessage(saveError));
    } finally {
      setLoading(false);
    }
  };

  const getInvitationLink = (invitationId, guestData) =>
    `${baseUrl}/invitacion/${guestData.slug}/${invitationId}`;

  const getQrValidationLink = (invitationId, guestId, wardId = "null") =>
    `${baseUrl}/confirmacionqr/${invitationId}/${guestId}/${wardId}`;

  return (
    <div className="admin-page">

      <div className="create-page-header">
        <p className="create-page-label">Panel de administración</p>
        <div className="create-page-rule" />
        <h2 className="create-page-title">Crear Invitación</h2>
        <p className="create-page-desc">
          Registra una familia con su invitado principal y acompañantes.
          Cada guardado genera una invitación independiente.
        </p>
      </div>

      <form className="admin-form" onSubmit={handleCreate}>

        <span className="create-section-label">Familia</span>
        <div className="field-block">
          <label htmlFor="familyAlias">Alias de familia</label>
          <input
            id="familyAlias"
            type="text"
            value={familyAlias}
            onChange={(e) => setFamilyAlias(e.target.value)}
            placeholder="Ej. Fam./Sr./Srta. García López"
          />
        </div>

        <span className="create-section-label">Invitado principal</span>
        <div className="guest-card">
          <div className="guest-grid">
            <input
              type="text"
              value={guest.name}
              onChange={(e) => updateGuestField("name", e.target.value)}
              placeholder="Nombre"
            />
            <input
              type="text"
              value={guest.slug}
              onChange={(e) => {
                setSlugTouched(true);
                updateGuestField("slug", slugify(e.target.value));
              }}
              placeholder="Slug"
            />
            <input
              type="text"
              value={guest.mobile}
              maxLength={10}
              inputMode="numeric"
              onChange={(e) =>
                updateGuestField("mobile", e.target.value.replace(/\D/g, ""))
              }
              placeholder="Teléfono (10 dígitos)"
            />
            <input
              type="text"
              value={guest.table}
              onChange={(e) => updateGuestField("table", e.target.value)}
              placeholder="Mesa (opcional)"
            />
          </div>

          <label className="checkbox-line">
            <input
              type="checkbox"
              checked={guest.confirmed}
              onChange={(e) => updateGuestField("confirmed", e.target.checked)}
            />
            Confirmado
          </label>

          <div className="wards-section">
            <div className="wards-header">
              <span className="create-section-label" style={{ marginBottom: 0 }}>
                Acompañantes
              </span>
              <button type="button" className="btn-secondary" onClick={addWard}>
                + Agregar
              </button>
            </div>

            {guest.wards.length === 0 && (
              <p className="ward-empty">Sin acompañantes registrados</p>
            )}

            {guest.wards.map((ward, wardIndex) => (
              <div className="ward-row" key={`ward-${wardIndex}`}>
                <input
                  type="text"
                  value={ward.name}
                  onChange={(e) => updateWardField(wardIndex, "name", e.target.value)}
                  placeholder="Nombre acompañante"
                />
                <input
                  type="text"
                  value={ward.table}
                  onChange={(e) => updateWardField(wardIndex, "table", e.target.value)}
                  placeholder="Mesa (opcional)"
                />
                <label className="checkbox-line">
                  <input
                    type="checkbox"
                    checked={ward.confirmed}
                    onChange={(e) =>
                      updateWardField(wardIndex, "confirmed", e.target.checked)
                    }
                  />
                  Conf.
                </label>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => removeWard(wardIndex)}
                >
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="action-row">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Guardando..." : "Guardar invitación"}
          </button>
        </div>

        {error && <p className="status error">{error}</p>}
        {success && <p className="status success">{success}</p>}
      </form>

      {createdInvitation && (
        <div className="saved-block">
          <p className="create-page-label" style={{ textAlign: "center" }}>
            Invitación registrada
          </p>
          <div className="create-page-rule" />

          <div className="invitation-card">
            <p className="saved-family-name">{createdInvitation.familyAlias}</p>
            <p className="saved-meta">ID · {createdInvitation.id}</p>

            {createdInvitation.guests.map((guestData) => (
              <div className="saved-guest" key={guestData.id}>
                <p className="saved-guest-name">{guestData.name}</p>
                <div className="saved-meta-row">
                  <span>Tel · {guestData.mobile || "—"}</span>
                  <span>Mesa · {guestData.table || "—"}</span>
                  <span>Confirmado · {guestData.confirmed ? "Sí" : "No"}</span>
                  <span>Slug · {guestData.slug}</span>
                </div>
                <div className="saved-links">
                  <a
                    className="saved-link"
                    href={getInvitationLink(createdInvitation.id, guestData)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Invitación
                  </a>
                  <a
                    className="saved-link"
                    href={getQrValidationLink(createdInvitation.id, guestData.id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    QR invitado
                  </a>
                </div>

                {(guestData.wards || []).length > 0 && (
                  <div className="saved-wards">
                    <span className="create-section-label" style={{ marginBottom: 8 }}>
                      Acompañantes
                    </span>
                    {(guestData.wards || []).map((ward) => (
                      <div
                        className="saved-ward-line"
                        key={`created-ward-${guestData.id}-${ward.id}`}
                      >
                        <span className="saved-ward-name">{ward.name}</span>
                        <div className="saved-meta-row">
                          <span>Mesa · {ward.table || "—"}</span>
                          <span>Confirmado · {ward.confirmed ? "Sí" : "No"}</span>
                        </div>
                        <div className="saved-links">
                          <a
                            className="saved-link"
                            href={getQrValidationLink(
                              createdInvitation.id,
                              guestData.id,
                              ward.id
                            )}
                            target="_blank"
                            rel="noreferrer"
                          >
                            QR acompañante
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePage;
